package songwritersnotebook.app.utils

import songwritersnotebook.app.data.RhymeRepository
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.viewmodels.EditorSettings
import songwritersnotebook.app.viewmodels.RhymeType
import songwritersnotebook.app.viewmodels.RhymeInfo
import songwritersnotebook.database.AppDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

data class RhymeResult(val word: String, val score: Int)

object RhymeService {
    private val searchCache = mutableMapOf<String, List<RhymeResult>>()

    suspend fun findRhymesInSong(database: AppDatabase, song: Song, settings: EditorSettings): Map<String, RhymeInfo> {
        if (!settings.rhymingEnabled) return emptyMap()

        val wordsInSong = mutableSetOf<String>()
        song.sections.forEach { section ->
            section.lines.forEach { line ->
                val words = line.text.replace(Regex("""\[.*?\]"""), "") 
                    .replace(Regex("""[^a-zA-Z0-9'\s]"""), "") 
                    .lowercase()
                    .split(Regex("""\s+"""))
                    .filter { it.isNotBlank() }

                if (settings.endOfLineOnly) {
                    words.lastOrNull()?.let { wordsInSong.add(it) }
                } else {
                    wordsInSong.addAll(words)
                }
            }
        }

        if (wordsInSong.isEmpty()) return emptyMap()

        val pronunciations = RhymeRepository.getPronunciations(database, wordsInSong.toList())

        val wordRelations = mutableMapOf<String, MutableSet<String>>()
        val rhymeTypesMap = mutableMapOf<String, MutableMap<String, RhymeType>>()

        wordsInSong.forEach {
            wordRelations[it] = mutableSetOf(it)
            rhymeTypesMap[it] = mutableMapOf()
        }

        val wordList = wordsInSong.toList()

        for (i in wordList.indices) {
            val wordA = wordList[i]
            val phonemesListA = pronunciations[wordA] ?: continue

            // Identity check: Word rhymes with itself PERFECTLY
            wordRelations[wordA]?.add(wordA)
            rhymeTypesMap[wordA]?.put(wordA, RhymeType.PERFECT)

            for (j in i + 1 until wordList.size) {
                val wordB = wordList[j]
                val phonemesListB = pronunciations[wordB] ?: continue

                var bestMatchType: RhymeType? = null
                for (phonemesA in phonemesListA) {
                    for (phonemesB in phonemesListB) {
                        val currentMatch = checkRhyme(phonemesA, phonemesB)
                        if (currentMatch == RhymeType.PERFECT) {
                            bestMatchType = RhymeType.PERFECT
                            break
                        } else if (currentMatch == RhymeType.SLANT) {
                            bestMatchType = RhymeType.SLANT
                        }
                    }
                    if (bestMatchType == RhymeType.PERFECT) break
                }

                if (bestMatchType != null) {
                    if (bestMatchType == RhymeType.SLANT && !settings.showSlantRhymes) {
                        continue
                    }
                    wordRelations[wordA]?.add(wordB)
                    wordRelations[wordB]?.add(wordA)
                    rhymeTypesMap[wordA]?.put(wordB, bestMatchType)
                    rhymeTypesMap[wordB]?.put(wordA, bestMatchType)
                }
            }
        }

        val visited = mutableSetOf<String>()
        val groups = mutableListOf<Set<String>>()

        for (word in wordsInSong) {
            if (!visited.contains(word) && (wordRelations[word]?.size ?: 0) > 1) {
                val currentGroup = mutableSetOf<String>()
                val queue = mutableListOf(word)
                visited.add(word)

                while (queue.isNotEmpty()) {
                    val current = queue.removeAt(0)
                    currentGroup.add(current)

                    wordRelations[current]?.forEach { neighbor ->
                        if (!visited.contains(neighbor)) {
                            visited.add(neighbor)
                            queue.add(neighbor)
                        }
                    }
                }
                groups.add(currentGroup)
            }
        }

        val rhymeMap = mutableMapOf<String, RhymeInfo>()
        val colors = listOf(
            "#E63946", // Vivid Red
            "#457B9D", // Steel Blue
            "#2A9D8F", // Medium Sea Green
            "#E76F51", // Burnt Sienna / Orange
            "#9B5DE5", // Amethyst Purple
            "#F15BB5", // Magenta Pink
            "#00BBF9", // Bright Cyan
            "#D4A373", // Warm Sand/Gold
            "#8338EC", // Blue-Violet
            "#52B788", // Mid-tone Spring Green
            "#D62828", // Darker Firebrick
            "#3A86FF"  // Azure/Neon Blue
        )

        groups.forEachIndexed { index, group ->
            val color = colors[index % colors.size]
            group.forEach { word ->
                val hasPerfectMatch = rhymeTypesMap[word]?.filter { it.key != word }?.values?.any { it == RhymeType.PERFECT } == true
                rhymeMap[word] = RhymeInfo(if (hasPerfectMatch) RhymeType.PERFECT else RhymeType.SLANT, color)
            }
        }

        return rhymeMap
    }

    private fun checkRhyme(phonemesA: String, phonemesB: String): RhymeType? {
        val suffixA = RhymeRepository.getStressedSuffix(phonemesA)
        val suffixB = RhymeRepository.getStressedSuffix(phonemesB)

        if (suffixA.isEmpty() || suffixB.isEmpty()) return null
        if (suffixA == suffixB) return RhymeType.PERFECT

        val vowelA = suffixA.split(Regex("""\s+""")).firstOrNull()
        val vowelB = suffixB.split(Regex("""\s+""")).firstOrNull()

        if (vowelA != null && vowelA == vowelB) {
            val syllablesA = suffixA.count { it.isDigit() }
            val syllablesB = suffixB.count { it.isDigit() }
            if (syllablesA == syllablesB) {
                return RhymeType.SLANT
            }
        }
        return null
    }

    suspend fun fetchRhymesForWord(database: AppDatabase, word: String): List<String>? {
        if (word.isBlank()) return emptyList()

        return withContext(Dispatchers.Default) {
            searchCache[word]?.let { return@withContext it.map { r -> r.word } }

            try {
                val resultsMap = RhymeRepository.getRhymes(database, word)
                val rhymeList = resultsMap.map { (w, score) ->
                    RhymeResult(w.lowercase(), score)
                }.sortedWith(
                    compareByDescending<RhymeResult> { it.score }
                        .thenBy { it.word }
                )
                searchCache[word] = rhymeList
                rhymeList.map { it.word }
            } catch (e: Exception) {
                null
            }
        }
    }
}
